/**
 * Strategy Display Helpers
 *
 * Comprehensive utility functions for displaying strategy information consistently across components.
 * Consolidates formatting, badge variants, and display logic from StrategyCard and StrategyTable.
 */

import { StrategyMetadata } from '../../types';
import {
  INTRADAY_DTE_COMPACT_LABELS,
  INTRADAY_EXIT_MODE,
  TRADING_TYPE,
} from '../../constants/tradingConstants';

export interface StrategyTradingInfo {
  tradingType?: string;
  intradayExitMode?: string;
  entryTradingDaysBeforeExpiry?: number;
  exitTradingDaysBeforeExpiry?: number;
}

/**
 * Canonical trading-mode classification — single source of truth.
 *
 * Rules (mirrored on backend `strategy_manager.py` validation + frontend
 * display helpers):
 *   INTRADAY + SAME_DAY (or missing exit mode) → 'INTRADAY'
 *   INTRADAY + NEXT_DAY_BTST                   → 'BTST_STBT'
 *   POSITIONAL + entry_tdbe == exit_tdbe       → 'INTRADAY'
 *     (positional in scheduling but intraday-on-the-target-day in
 *      behaviour — enter and exit on the same DTE)
 *   POSITIONAL + entry_tdbe != exit_tdbe       → 'POSITIONAL'
 *   anything else                              → null  (unknown trading_type
 *      or missing TDBE on POSITIONAL — callers render the raw value or a
 *      sensible default)
 *
 * All trading-type label helpers (`getDerivedTradingType`,
 * `getDerivedTradingTypeShort`, `formatTradingType` in scheduleSummary.ts)
 * route through this classifier. Don't add new rules — extend here and
 * update all consumers in lockstep.
 */
export type TradingMode = 'INTRADAY' | 'BTST_STBT' | 'POSITIONAL';

export const classifyTradingType = (
  strategy: StrategyTradingInfo,
): TradingMode | null => {
  const t = strategy.tradingType;
  if (t === TRADING_TYPE.INTRADAY) {
    if (strategy.intradayExitMode === INTRADAY_EXIT_MODE.NEXT_DAY_BTST) {
      return 'BTST_STBT';
    }
    return 'INTRADAY';
  }
  if (t === TRADING_TYPE.POSITIONAL) {
    const entry = strategy.entryTradingDaysBeforeExpiry;
    const exit = strategy.exitTradingDaysBeforeExpiry;
    // Defensive: both TDBE values must be present and equal for the
    // same-day-on-DTE collapse. Half-defined or undefined inputs fall
    // through to 'POSITIONAL' (matches the legacy 2-arg `formatTradingType`
    // behaviour for schedule callers that don't have TDBE on their payload).
    if (entry != null && exit != null && entry === exit) {
      return 'INTRADAY';
    }
    return 'POSITIONAL';
  }
  return null;
};

/**
 * Calculate derived trading type (Title Case, used on the baskets page list
 * + table).
 *
 * @param strategy Strategy object with trading configuration
 * @returns Human-readable derived trading type
 */
export const getDerivedTradingType = (strategy: StrategyTradingInfo): string => {
  const mode = classifyTradingType(strategy);
  if (mode === 'INTRADAY') return 'Intraday';
  if (mode === 'BTST_STBT') return 'BTST/STBT';
  if (mode === 'POSITIONAL') return 'Positional';
  // Fallback for unknown trading_type — surface the raw value so the UI
  // doesn't silently lose information.
  return strategy.tradingType || 'Standard';
};

/**
 * Short abbreviation for compact displays (StrategyRowView chip).
 *
 * @param strategy Strategy object with trading configuration
 * @returns Short abbreviation
 */
export const getDerivedTradingTypeShort = (strategy: StrategyTradingInfo): string => {
  const mode = classifyTradingType(strategy);
  if (mode === 'INTRADAY') return 'INTRA';
  if (mode === 'BTST_STBT') return 'BTST/STBT';
  if (mode === 'POSITIONAL') return 'POS';
  return 'STD';
};

/**
 * Get color variant for the derived trading type badge
 *
 * @param strategy Strategy object with trading configuration
 * @returns Color variant string
 */
export const getDerivedTradingTypeVariant = (strategy: StrategyTradingInfo): 'default' | 'success' | 'warning' | 'danger' | 'info' => {
  const derivedType = getDerivedTradingType(strategy);

  // `getDerivedTradingType` never emits the string 'Positional (Same Day)' —
  // it folds single-day positional into 'Intraday' (entry/exit DTE equal).
  // The case is kept off the switch so it doesn't read as a live option.
  switch (derivedType) {
    case 'Intraday':
      return 'success';
    case 'BTST/STBT':
      return 'warning';
    case 'Positional':
      return 'info';
    default:
      return 'default';
  }
};

/**
 * FORMAT UTILITIES - Shared formatting functions
 */

/**
 * Format days array to readable string
 *
 * @param days Array of day names
 * @returns Formatted day string (e.g., "MON, TUE, WED")
 */
export const formatDays = (days?: string[]): string => {
  if (!days || days.length === 0) return 'Not set';

  const dayMap: { [key: string]: string } = {
    'MONDAY': 'MON', 'TUESDAY': 'TUE', 'WEDNESDAY': 'WED',
    'THURSDAY': 'THU', 'FRIDAY': 'FRI', 'SATURDAY': 'SAT', 'SUNDAY': 'SUN'
  };

  return days.map(day => dayMap[day.toUpperCase()] || day.toUpperCase()).join(', ');
};

/**
 * Format entry selection for read-only display.
 *
 * Branches on entrySelectionMode:
 *   WEEKDAY (default, legacy): returns formatDays(entryDays) — same as today.
 *   DTE: returns compact "D4, D3, D2, D1, D0" labels for at-a-glance scanning.
 *        Descending order mirrors the editor chip layout (left-to-right is
 *        calendar progression toward expiry day).
 *
 * Mirrors the backend runtime gate in entry_schedule.is_entry_scheduled_at.
 * Missing mode → WEEKDAY behaviour, so legacy rows render unchanged.
 */
export const formatEntrySelection = (strategy: {
  entrySelectionMode?: 'WEEKDAY' | 'DTE';
  entryDays?: string[];
  entryDtes?: number[];
}): string => {
  if (strategy.entrySelectionMode === 'DTE') {
    const dtes = strategy.entryDtes ?? [];
    if (dtes.length === 0) return 'Not set';
    return dtes
      .slice()
      .sort((a, b) => b - a)
      .map(d => INTRADAY_DTE_COMPACT_LABELS[d] ?? `D${d}`)
      .join(', ');
  }
  return formatDays(strategy.entryDays);
};

/**
 * Format time to readable format
 *
 * @param time Time string (HHMM or HH:MM format)
 * @returns Formatted time string (HH:MM)
 */
export const formatTime = (time?: string): string => {
  if (!time) return 'Not set';

  // If it's already in HH:MM format, return as is
  if (time.includes(':')) return time;

  // If it's in HHMM format, add colon
  if (time.length === 4) {
    return `${time.substring(0, 2)}:${time.substring(2, 4)}`;
  }

  return time;
};

/**
 * BADGE VARIANT UTILITIES - Consistent badge color variants
 */

/**
 * Get badge variant for product type
 *
 * @param product Product type (MIS, NRML)
 * @returns Badge variant string
 */
export const getProductBadgeVariant = (product?: string): 'default' | 'success' | 'warning' | 'danger' | 'info' => {
  switch (product?.toUpperCase()) {
    case 'MIS': return 'success';
    case 'NRML': return 'warning';
    default: return 'default';
  }
};

/**
 * Get badge variant for strategy status
 *
 * @param status Strategy status
 * @returns Badge variant string
 */
export const getStatusBadgeVariant = (status: string): 'default' | 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case 'ACTIVE': return 'success';
    case 'PAUSED': return 'warning';
    case 'COMPLETED': return 'default';
    default: return 'default';
  }
};

/**
 * ADVANCED FEATURES DISPLAY - Consolidated logic for showing strategy features
 */

/**
 * Generate advanced features display for strategy cards
 * Includes derived trading type as first feature
 *
 * @param strategy Strategy object
 * @returns Formatted features string
 */
export const getAdvancedFeaturesWithDerivedType = (strategy: StrategyMetadata): string => {
  const features = [];

  // Always show derived trading type as the first feature
  features.push(getDerivedTradingType(strategy));

  // Check for advanced features based on available strategy data
  if (strategy.moveSlToCost) features.push('MSLC');
  if (strategy.rangeBreakout) features.push('ORB');

  // Check for strategy-level risk management features
  // If enabled is undefined but value exists and > 0, treat as enabled
  if (strategy.targetProfit && typeof strategy.targetProfit.value === 'number' && strategy.targetProfit.value > 0 &&
      (strategy.targetProfit.enabled !== false)) {
    const tpValue = strategy.targetProfit.value;
    const tpType = strategy.targetProfit.type;
    // Format display based on type
    let displayText = `TP: ${tpValue}`;
    if (tpType === "COMBINED_PREMIUM_PERCENT") displayText += "% Prem";
    else if (tpType === "TOTAL_MTM") displayText += " Pts.";
    else displayText += "pts";
    features.push(displayText);
  }

  if (strategy.stopLoss && typeof strategy.stopLoss.value === 'number' && strategy.stopLoss.value > 0 &&
      (strategy.stopLoss.enabled !== false)) {
    const slValue = strategy.stopLoss.value;
    const slType = strategy.stopLoss.type;
    // Format display based on type
    let displayText = `SL: ${slValue}`;
    if (slType === "TOTAL_MTM") displayText += " Pts.";
    else if (slType === "COMBINED_PREMIUM_PERCENT") displayText += "% Prem";
    else displayText += "pts";
    features.push(displayText);
  }

  return features.join(', ');
};

/**
 * Generate advanced features display for strategy tables
 * Does NOT include derived trading type (shown separately in table)
 *
 * @param strategy Strategy object
 * @returns Formatted features string or "None" if no features
 */
export const getAdvancedFeaturesForTable = (strategy: StrategyMetadata): string => {
  const features = [];

  // Check for advanced features based on available strategy data
  if (strategy.moveSlToCost) features.push('MSLC');
  if (strategy.rangeBreakout) features.push('ORB');

  // Check for strategy-level risk management features
  if (strategy.targetProfit && typeof strategy.targetProfit.value === 'number' && strategy.targetProfit.value > 0 &&
      (strategy.targetProfit.enabled !== false)) {
    const tpValue = strategy.targetProfit.value;
    const tpType = strategy.targetProfit.type;
    let displayText = `TP: ${tpValue}`;
    if (tpType === "COMBINED_PREMIUM_PERCENT") displayText += "% Prem";
    else if (tpType === "TOTAL_MTM") displayText += " Pts.";
    else displayText += "pts";
    features.push(displayText);
  }

  if (strategy.stopLoss && typeof strategy.stopLoss.value === 'number' && strategy.stopLoss.value > 0 &&
      (strategy.stopLoss.enabled !== false)) {
    const slValue = strategy.stopLoss.value;
    const slType = strategy.stopLoss.type;
    let displayText = `SL: ${slValue}`;
    if (slType === "TOTAL_MTM") displayText += " Pts.";
    else if (slType === "COMBINED_PREMIUM_PERCENT") displayText += "% Prem";
    else displayText += "pts";
    features.push(displayText);
  }

  return features.length > 0 ? features.join(', ') : 'None';
};